/**
 * Venues' scope picker: the shared control, wired to this page.
 *
 * The Overview adapter's twin. What differs is everything page-specific: where
 * the regions come from (`/api/v1/regions`, flattened) and how a region is
 * addressed.
 *
 * Venues addresses by a single query parameter, `?region=<slug>`, at any depth,
 * because the roster endpoint rolls up the subtree. Overview spells out the
 * ancestor path instead. The two models coexist deliberately: Overview's slugs
 * need only be unique among siblings, Venues' are globally unique.
 *
 * Every link keeps the rest of the address (the open-tasks filter and any other
 * parameter), so changing region never silently drops what the viewer set.
 */
import { useMemo } from 'react';
import { useSearchParams } from 'react-router-dom';

import { usePermissions } from '../../../../hooks/usePermissions';
import { Permission } from '../../../../types/permissions';
import { flattenRegions, useRegionHierarchy } from '../../primitives/scope/region-hierarchy';
import { ALL_REGIONS } from '../../primitives/scope/region-param';
import type { ScopeHrefFor } from '../../primitives/scope/scope.types';
import { ScopePicker } from '../../primitives/scope/ScopePicker';
import { useOwnRegions } from '../../primitives/scope/use-own-regions';

export interface VenuesScopePickerProps {
  label: string;
  /** The region currently being viewed; null at all-regions scope. */
  currentRegionId: string | null;
  /** Venues uses the app's own phone tier — there is no calendar to squeeze. */
  isPhone: boolean;
  /** The viewer is looking at the union of their own regions. */
  isOwnRegions: boolean;
}

const VENUES_PATH = '/admin/venues';

/** The current address with `region` set, or removed when `region` is null. */
function addressWith(current: URLSearchParams, region: string | null): string {
  const next = new URLSearchParams(current);
  if (region === null) next.delete('region');
  else next.set('region', region);
  const query = next.toString();
  return query ? `${VENUES_PATH}?${query}` : VENUES_PATH;
}

export function VenuesScopePicker({
  label,
  currentRegionId,
  isPhone,
  isOwnRegions,
}: VenuesScopePickerProps) {
  const { data } = useRegionHierarchy();
  const { hasAnyPermission } = usePermissions();
  const { own } = useOwnRegions();
  const [params] = useSearchParams();

  // A region's address is its slug. All regions is the explicit `region=all`:
  // a bare address is where the own-region default applies, so choosing All
  // regions has to be written down for it to stick.
  const hrefFor: ScopeHrefFor = useMemo(
    () => (region) => addressWith(params, region ? region.slug : ALL_REGIONS),
    [params],
  );

  return (
    <ScopePicker
      label={label}
      currentRegionId={currentRegionId}
      regions={data ? flattenRegions(data) : []}
      hrefFor={hrefFor}
      isPhone={isPhone}
      showAllRegions={hasAnyPermission(Permission.BREADTH_READ_ALL, Permission.READ_ALL)}
      // A bare address is the union default, so it survives the own-region rule.
      ownRegions={
        own.length > 1
          ? { href: addressWith(params, null), count: own.length, isHere: isOwnRegions }
          : undefined
      }
    />
  );
}
